"use client"

import { cx } from "@shared/components/untitledui/util/cx"
import { Check, ChevronRight, DotsVertical } from "@untitledui/icons"
import { motion, useReducedMotion } from "motion/react"
import {
  type FC,
  type RefAttributes,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"
import type {
  ButtonProps as AriaButtonProps,
  MenuItemProps as AriaMenuItemProps,
  MenuProps as AriaMenuProps,
  PopoverProps as AriaPopoverProps,
  SeparatorProps as AriaSeparatorProps,
  MenuItemRenderProps,
  PopoverRenderProps,
} from "react-aria-components"
import {
  Button as AriaButton,
  Header as AriaHeader,
  Menu as AriaMenu,
  MenuItem as AriaMenuItem,
  MenuSection as AriaMenuSection,
  MenuTrigger as AriaMenuTrigger,
  Popover as AriaPopover,
  Separator as AriaSeparator,
  OverlayTriggerStateContext,
  PopoverContext,
  useSlottedContext,
} from "react-aria-components"
import { Avatar } from "../avatar/avatar"
import { CheckboxBase } from "../checkbox/checkbox"
import { RadioButtonBase } from "../radio-buttons/radio-buttons"
import { ToggleBase } from "../toggle/toggle"
import { computeArrowGeometry } from "./arrow-geometry"

interface DropdownItemProps extends AriaMenuItemProps {
  /** The label of the item to be displayed. */
  label?: string
  /** An addon to be displayed on the right side of the item. */
  addon?: string
  /** If true, the item will not have any styles. */
  unstyled?: boolean
  /** An icon to be displayed on the left side of the item. */
  icon?: FC<{ className?: string }>
  /** Avatar URL to be displayed on the left side of the item. */
  avatarUrl?: string
  /** The selection indicator to be displayed on the item. */
  selectionIndicator?: "checkmark" | "checkbox" | "radio" | "toggle" | "none"
  /** Cp mod: Show a blue background when "active". */
  isActive?: boolean
}

const SelectionIndicator = ({
  indicator,
  state,
  className,
}: {
  indicator: NonNullable<DropdownItemProps["selectionIndicator"]>
  state: MenuItemRenderProps
  className?: string
}) => {
  switch (indicator) {
    case "checkmark":
      return (
        <Check
          aria-hidden="true"
          className={cx(
            "size-4 shrink-0 stroke-[2.25px] text-fg-brand-primary",
            !state.isSelected && "invisible",
            className,
          )}
        />
      )
    case "checkbox":
      return (
        <CheckboxBase
          isSelected={state.isSelected && !state.hasSubmenu}
          isIndeterminate={state.isSelected && state.hasSubmenu}
          size="sm"
          className={cx("shrink-0", className)}
        />
      )
    case "radio":
      return <RadioButtonBase isSelected={state.isSelected} className={cx("shrink-0", className)} />
    case "toggle":
      return (
        <ToggleBase
          slim
          size="sm"
          isSelected={state.isSelected}
          className={cx("shrink-0", className)}
        />
      )
    case "none":
      return null
    default: {
      const _exhaustive: never = indicator
      return _exhaustive
    }
  }
}

const DropdownItem = ({
  label,
  children,
  addon,
  icon: Icon,
  avatarUrl,
  unstyled,
  selectionIndicator = "checkmark",
  isActive,
  ...props
}: DropdownItemProps) => {
  // Tag each row with its visible label so tests can address rows by label.
  const testAttributes = label ? { "data-test": "menu row", "data-label": label } : {}

  if (unstyled) {
    return <AriaMenuItem id={label} textValue={label} {...testAttributes} {...props} />
  }

  return (
    <AriaMenuItem
      textValue={label}
      {...testAttributes}
      {...props}
      className={(state) =>
        cx(
          "group block cursor-pointer px-1.5 py-px outline-hidden",
          state.isDisabled && "cursor-not-allowed opacity-50",
          typeof props.className === "function" ? props.className(state) : props.className,
        )
      }
    >
      {(state) => (
        <div
          className={cx(
            "relative flex items-center rounded-md px-2.5 py-2 outline-focus-ring transition duration-100 ease-linear",
            !state.isDisabled && "group-hover:bg-primary_hover",
            state.isFocused && "bg-primary_hover",
            state.isFocusVisible && "outline-2 -outline-offset-2",
            state.hasSubmenu && "pr-1.5",
            isActive && "bg-blue-100!",
          )}
        >
          {state.selectionMode !== "none" && !avatarUrl && !Icon && (
            <SelectionIndicator indicator={selectionIndicator} state={state} className="mr-2" />
          )}

          {avatarUrl && (
            <div className="mr-2 flex size-4 items-center justify-center">
              <Avatar aria-hidden="true" size="xs" src={avatarUrl} alt={label} className="size-5" />
            </div>
          )}

          {Icon && (
            <Icon
              aria-hidden="true"
              className="mr-2 size-4 shrink-0 stroke-[2.25px] text-fg-quaternary"
            />
          )}

          <span
            className={cx(
              // Cp mod: Remove truncate, to avoid cutting off text.
              // Callers should use an explicit width on the Dropdown.Popover to prevent it from wrapping too aggressively.
              "grow text-sm font-semibold text-secondary",
              state.isFocused && "text-secondary_hover",
            )}
          >
            {label || (typeof children === "function" ? children(state) : children)}
          </span>

          {addon && (
            <span className="ml-1 shrink-0 pr-1 text-xs font-medium text-quaternary">{addon}</span>
          )}

          {state.selectionMode !== "none" && (avatarUrl || Icon) && (
            <SelectionIndicator indicator={selectionIndicator} state={state} className="ml-1" />
          )}

          {state.hasSubmenu && (
            <ChevronRight
              aria-hidden="true"
              className="ml-auto size-4 shrink-0 stroke-[2.25px] text-fg-quaternary"
            />
          )}
        </div>
      )}
    </AriaMenuItem>
  )
}

const DropdownMenu = <T extends object>(props: AriaMenuProps<T>) => {
  return (
    <AriaMenu
      {...props}
      className={(state) =>
        cx(
          "h-min min-h-0 overflow-y-auto py-1 outline-hidden select-none",
          typeof props.className === "function" ? props.className(state) : props.className,
        )
      }
    />
  )
}

const POPOVER_CLASSES =
  // React Aria puts its computed max-height on the outer popover element; inherit it here so
  // the box (and the Menu inside it) actually stops at the viewport and scrolls.
  "bg-primary flex max-h-[inherit] w-62 origin-(--trigger-anchor-point) flex-col rounded-2xl shadow-menu will-change-transform"

interface DropdownPopoverProps extends AriaPopoverProps {
  /**
   * Where the menu opens relative to the trigger: a side (`"top"` / `"bottom"` /
   * `"left"` / `"right"`), optionally followed by a cross-axis alignment — e.g.
   * `"right top"` opens to the right with the menu's top corner anchored at the
   * trigger, `"bottom"` opens below, centered. Defaults to `"bottom right"`. React
   * Aria flips the side when the menu would leave the viewport — resolved once per
   * open, then held, so a menu whose content resizes can't change sides mid-open.
   * Also drives which edge the {@link arrow} sits on and where it points.
   */
  placement?: AriaPopoverProps["placement"]
  /**
   * Show a triangle on the trigger-facing edge of the menu, pointing at the center of the
   * trigger. It tracks the trigger even when the menu shifts or flips to stay on screen.
   * On corner-aligned placements (e.g. the default `"bottom right"`) the menu slides so
   * the arrow sits 1rem in from that corner; centered placements (e.g. `"bottom"`) center
   * it on the edge instead. Alignment math assumes LTR.
   */
  arrow?: boolean
  /**
   * Dim everything behind the menu with a translucent black wash that fades in and out
   * with the open/close animation, making the menu pop. Purely visual: it ignores pointer
   * events, so clicks still land on React Aria's own outside-click underlay.
   */
  scrim?: boolean
  /**
   * Keep the top edge where the menu opened, for menus the user expands in place. Leave it
   * off when the content arrives on its own — React Aria should re-place those as they fill in.
   */
  holdsTop?: boolean
}

/** Where the menu opens relative to the trigger; also drives where the arrow points. */
const DEFAULT_PLACEMENT = "bottom right" as const

/**
 * A triangle welded to the popover's edge, pointing at the trigger. React Aria publishes
 * the point the menu emanates from — the trigger's anchor projected onto the popover box,
 * recomputed whenever the menu shifts or flips to stay on screen — as the
 * `--trigger-anchor-point` CSS variable (the same one the box scales from via
 * `transform-origin`). Translating a zero-size wrapper by it lands exactly on the aim
 * point on the trigger-facing edge — the variable's other component is pinned to that
 * edge (0 or the full box size), so the edge comes for free — and the resolved
 * `data-placement` on the popover element picks the protrusion direction. Everything
 * stays a plain child of the animated box, so the arrow rides the open/close spring by
 * construction — unlike a React Aria `OverlayArrow`, whose position arrives from a
 * separate post-layout pass.
 *
 * The fill matches the popover background and the path's base edge is open, so it reads
 * as one continuous shape with the menu; it tucks 1px under the box so antialiasing can't
 * open a seam.
 */
const PopoverArrow = ({ nudge, axis }: { nudge: number; axis: "x" | "y" }) => (
  <span
    aria-hidden
    // Arbitrary property, not `translate-(--…)`: the translate utility writes its value
    // once per axis, which mangles a two-value variable into invalid CSS.
    className="absolute top-0 left-0 [translate:var(--trigger-anchor-point)]"
    // On corner-aligned menus the anchor marks the trigger's near edge, not its center —
    // `nudge` walks the arrow back along the cross axis to point at the center.
    style={nudge ? (axis === "x" ? { marginLeft: nudge } : { marginTop: nudge }) : undefined}
  >
    {/*
     * The svg's layout box is 24×12 regardless of rotation, so the top/bottom sides center
     * it with a translate while the left/right sides place it with explicit offsets that
     * put the rotated glyph's base 1px inside the menu edge, centered on the aim point.
     */}
    <svg
      width={24}
      height={12}
      viewBox="0 0 24 12"
      className={cx(
        "absolute cc-hidden fill-bg-primary forced-colors:fill-[Canvas]",
        "in-placement-top:-top-px in-placement-top:block in-placement-top:-translate-x-1/2",
        "in-placement-bottom:-bottom-px in-placement-bottom:block in-placement-bottom:-translate-x-1/2 in-placement-bottom:rotate-180",
        "in-placement-left:top-[-6px] in-placement-left:left-[-7px] in-placement-left:block in-placement-left:-rotate-90",
        "in-placement-right:top-[-6px] in-placement-right:left-[-17px] in-placement-right:block in-placement-right:rotate-90",
      )}
    >
      <path d="M0 0 L12 12 L24 0" />
    </svg>
  </span>
)

/**
 * Reports the popover's resolved side (from React Aria's positioning pass) back up.
 * Rendered inside the popover's render prop, which re-runs on position updates even
 * when DropdownPopover itself doesn't re-render — so a plain ref couldn't be watched.
 * React Aria leaves the placement unset until the overlay has actually been positioned,
 * so the first reported side is the post-flip one — and by the time this effect runs,
 * the pass has also written its resolved max height onto the popover element.
 */
const LockResolvedPosition = ({
  side,
  onResolved,
}: {
  side: PopoverRenderProps["placement"]
  onResolved: (side: PopoverRenderProps["placement"]) => void
}) => {
  useEffect(() => {
    onResolved(side)
  }, [side, onResolved])
  return null
}

/**
 * Both open and close are real Motion springs. React Aria only keeps a closing overlay
 * mounted long enough to finish a *CSS* animation on its own, so — per React Aria's
 * Motion guide — the trigger's open state is fed to the popover's `isExiting` prop,
 * which holds it in the DOM until Motion reports the close is done. This hook owns the
 * hold: `isExiting` goes false once `onExitDone` has been called `expectedExits` times
 * (once per exit animation running for this close).
 */
const useHeldExit = ({ isOpen, expectedExits }: { isOpen: boolean; expectedExits: number }) => {
  const [exitsDone, setExitsDone] = useState(0)
  // A popover whose trigger renders closed must not start out "exiting": it would mount
  // invisibly with nothing animating, so no completion callback ever ends the hold, and
  // the phantom popover sits over the page eating clicks. Exiting requires a prior open.
  const [hasOpened, setHasOpened] = useState(isOpen)
  // Adjust state during render (React's sanctioned pattern) so reopening cancels a
  // pending exit and the next close starts with a fresh hold.
  if (isOpen && !hasOpened) setHasOpened(true)
  if (isOpen && exitsDone > 0) setExitsDone(0)
  return {
    isExiting: hasOpened && !isOpen && exitsDone < expectedExits,
    onExitDone: () => setExitsDone((n) => n + 1),
  }
}

const DropdownPopover = (props: DropdownPopoverProps) => {
  const reduceMotion = useReducedMotion()
  // Open/closed state from whichever React Aria trigger owns this popover (MenuTrigger,
  // DialogTrigger, …); PopoverContext additionally carries the trigger element.
  const triggerState = useContext(OverlayTriggerStateContext)
  const popoverCtx = useSlottedContext(PopoverContext)
  const { className, children, arrow, scrim, holdsTop, ...popoverProps } = props

  // Without a trigger state (a standalone popover) the entry still animates but the
  // exit can't be held: isOpen never goes false, so isExiting stays false.
  const isOpen = triggerState?.isOpen ?? true
  // The scrim fades over a longer duration than the menu, so the hold waits on both.
  const { isExiting, onExitDone } = useHeldExit({ isOpen, expectedExits: scrim ? 2 : 1 })
  // Shared by every exit animation: only the close counts toward releasing the hold.
  const onExitAnimationComplete = (definition: unknown) => {
    if (definition === "hidden") onExitDone()
  }

  // An explicit triggerRef prop overrides the trigger from context for positioning
  // (mirroring React Aria's own precedence), so measure the same element. Measured once
  // per render: if the trigger resizes while the menu is open, the arrow offsets are
  // stale until the next render — fine for menus.
  const triggerEl = (popoverProps.triggerRef ?? popoverCtx?.triggerRef)?.current
  const requestedPlacement = popoverProps.placement ?? DEFAULT_PLACEMENT
  const { crossAxis, crossOffset, nudge } = computeArrowGeometry(
    requestedPlacement,
    arrow ? triggerEl?.getBoundingClientRect() : undefined,
  )

  // Cp mod: the side is resolved once per open, then held. React Aria re-runs its flip
  // logic whenever the popover resizes, so a menu that opened above the trigger (no room
  // below) would teleport below it the moment its content shrinks enough to fit — e.g.
  // the lesson menu drilling into a short view. The first resolved side wins until the
  // popover has fully closed.
  const [lockedSide, setLockedSide] = useState<"top" | "bottom" | "left" | "right" | null>(null)
  if (!isOpen && !isExiting && lockedSide !== null) setLockedSide(null)

  // Cp mod (holdsTop): React Aria re-places the popover whenever it resizes, so without a
  // height cap a menu the user expands walks up the screen. Which cap depends on the edge
  // the box hangs from: a menu flipped to side "top" hangs from its BOTTOM, so it is capped
  // at the height it opened at; otherwise the top is anchored and React Aria's own resolved
  // max height is the room it may grow into.
  const popoverRef = useRef<HTMLElement | null>(null)
  const [lockedMaxHeight, setLockedMaxHeight] = useState<number | null>(null)
  if (!isOpen && !isExiting && lockedMaxHeight !== null) setLockedMaxHeight(null)

  const lockPosition = useCallback(
    (side: PopoverRenderProps["placement"]) => {
      if (side !== "top" && side !== "bottom" && side !== "left" && side !== "right") return
      setLockedSide((current) => current ?? side)
      if (!holdsTop) return
      const popover = popoverRef.current
      if (!popover) return
      if (side === "top") {
        const opened = popover.getBoundingClientRect().height
        if (opened > 0) setLockedMaxHeight((current) => current ?? opened)
        return
      }
      // The positioning pass writes its number in px; until it runs the element carries a
      // "100vh" placeholder, which parses to the number 100 — locking the menu to 100px.
      const room = popover.style.maxHeight
      if (!room.endsWith("px")) return
      const px = Number.parseFloat(room)
      if (Number.isFinite(px)) setLockedMaxHeight((current) => current ?? px)
    },
    [holdsTop],
  )
  const [, requestedAlignment] = requestedPlacement.split(" ")
  const placement = lockedSide
    ? ((requestedAlignment
        ? `${lockedSide} ${requestedAlignment}`
        : lockedSide) as AriaPopoverProps["placement"])
    : requestedPlacement

  const resolveClassName = (state: PopoverRenderProps) =>
    cx(
      POPOVER_CLASSES,
      // The arrow sits just outside the box edge, so it can't be clipped; without one
      // we keep the box clipping so long menus scroll inside the rounded corners.
      arrow ? "overflow-visible" : "overflow-auto",
      typeof className === "function"
        ? className({ ...state, defaultClassName: undefined })
        : className,
    )

  // Fast fade + subtle scale zooming from the trigger anchor (the shape Motion
  // recommends for menus), with a soft, low-bounce spring. Open and close are
  // symmetric: same scale and spring, reversed. The popover stays a plain React
  // Aria overlay (positioning + `isExiting`); the spring runs on an inner div,
  // since motion.create(Popover) hits a prop-name type clash in strict TS.
  const transition = reduceMotion
    ? { duration: 0 }
    : { type: "spring" as const, visualDuration: 0.14, bounce: 0.15 }
  // The scrim washes in more slowly than the menu, matching the legacy
  // .dialog-backdrop fade (0.5s ease-in-out).
  const scrimTransition = reduceMotion
    ? { duration: 0 }
    : { duration: 0.5, ease: "easeInOut" as const }

  return (
    <AriaPopover
      ref={popoverRef}
      // Gap between trigger and menu for the arrow (12 tall, 1px tucked under the box).
      offset={arrow ? 12 : undefined}
      crossOffset={crossOffset}
      // Hold the popover in the DOM until the close animations finish.
      isExiting={isExiting}
      {...popoverProps}
      // After the spread: once a side is locked, it overrides the requested placement
      // (and flipping turns off) so the menu can't change sides while open.
      placement={placement}
      shouldFlip={lockedSide === null ? popoverProps.shouldFlip : false}
      maxHeight={lockedMaxHeight ?? popoverProps.maxHeight}
    >
      {(state) => (
        <>
          <LockResolvedPosition side={state.placement} onResolved={lockPosition} />
          {/*
           * Outside the transformed menu box (a transform would re-root position: fixed),
           * as a plain child of the popover so it mounts/unmounts — and, via `isExiting`
           * above, fades out — together with the menu.
           */}
          {scrim && (
            <motion.div
              aria-hidden
              className="pointer-events-none fixed inset-0 bg-black/10"
              initial="hidden"
              animate={isOpen ? "visible" : "hidden"}
              variants={{ visible: { opacity: 1 }, hidden: { opacity: 0 } }}
              transition={scrimTransition}
              onAnimationComplete={onExitAnimationComplete}
            />
          )}
          <motion.div
            // Prevent pointer events on a closing menu, which still exists until
            // its animations finish (isExiting).
            // Otherwise double clicking a menu item executes it twice, and in some
            // cases RAC steals focus onto the dropdown trigger (via the exiting menu item).
            className={cx(resolveClassName(state), !isOpen && "pointer-events-none")}
            initial="hidden"
            animate={isOpen ? "visible" : "hidden"}
            variants={{
              visible: { opacity: 1, scale: 1 },
              hidden: { opacity: 0, scale: reduceMotion ? 1 : 0.97 },
            }}
            transition={transition}
            onAnimationComplete={onExitAnimationComplete}
          >
            {/* Inside the animated box so it scales + fades together with the menu. */}
            {arrow && <PopoverArrow nudge={nudge} axis={crossAxis} />}
            {typeof children === "function"
              ? children({ ...state, defaultChildren: undefined })
              : children}
          </motion.div>
        </>
      )}
    </AriaPopover>
  )
}

const DropdownSeparator = (props: AriaSeparatorProps) => {
  return (
    <AriaSeparator
      {...props}
      className={cx("my-1 h-px w-full bg-border-secondary", props.className)}
    />
  )
}

const DropdownDotsButton = (props: AriaButtonProps & RefAttributes<HTMLButtonElement>) => {
  return (
    <AriaButton
      {...props}
      aria-label="Open menu"
      className={(state) =>
        cx(
          "cursor-pointer rounded-md text-fg-quaternary outline-focus-ring transition duration-100 ease-linear",
          (state.isPressed || state.isHovered) && "text-fg-quaternary_hover",
          (state.isPressed || state.isFocusVisible) && "outline-2 outline-offset-2",
          typeof props.className === "function" ? props.className(state) : props.className,
        )
      }
    >
      <DotsVertical className="size-5 transition-inherit-all" />
    </AriaButton>
  )
}

export const Dropdown = {
  Root: AriaMenuTrigger,
  Popover: DropdownPopover,
  Menu: DropdownMenu,
  Section: AriaMenuSection,
  SectionHeader: AriaHeader,
  Item: DropdownItem,
  Separator: DropdownSeparator,
  DotsButton: DropdownDotsButton,
}
